import React, { useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCulturalEventsAPI } from './hooks/useCulturalEventsAPI';
import { CulturalEventCard } from './Public/CulturalEventCard';
import { LoadingSpinner } from './Shared/LoadingSpinner';
import { ErrorMessage } from './Shared/ErrorMessage';
import { useTigerLilyAnimation } from './hooks/useTigerLilyAnimation';
import { CulturalEvent } from './constants/culturalEventsTypes';
import './CulturalEventsSection.css';

// Sort events by date (day-month) and time
const sortEventsByDate = (events: CulturalEvent[]): CulturalEvent[] => {
  if (!events || events.length === 0) return [];
  
  console.log('🔄 Sorting events. Original order:', events.map(e => ({
    title: e.title.substring(0, 30),
    date: e.date,
    day: new Date(e.date).getDate(),
    month: new Date(e.date).getMonth() + 1
  })));
  
  const sorted = [...events].sort((a, b) => {
    try {
      // Parse dates from strings
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      
      // Check if dates are valid
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        console.warn('Invalid date found in events:', { 
          a: { date: a.date, parsed: dateA }, 
          b: { date: b.date, parsed: dateB } 
        });
        return 0;
      }
      
      // Get month and day for comparison (ignore year - sort by day-month only)
      // This assumes all events are in the same year (2026) for display purposes
      const monthA = dateA.getMonth();
      const dayA = dateA.getDate();
      
      const monthB = dateB.getMonth();
      const dayB = dateB.getDate();
      
      // Compare month first
      if (monthA !== monthB) {
        return monthA - monthB;
      }
      
      // Then day (ignoring year)
      if (dayA !== dayB) {
        return dayA - dayB;
      }
      
      // If same date, sort by time
      const timeA = a.time || '00:00:00';
      const timeB = b.time || '00:00:00';
      return timeA.localeCompare(timeB);
    } catch (error) {
      console.error('Error sorting events:', error, { a: a.date, b: b.date });
      return 0;
    }
  });
  
  console.log('✅ Sorted events. New order:', sorted.map(e => ({
    title: e.title.substring(0, 30),
    date: e.date,
    day: new Date(e.date).getDate(),
    month: new Date(e.date).getMonth() + 1
  })));
  
  return sorted;
};

export const CulturalEventsSection: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { events, loading, error, getUpcomingEvents } = useCulturalEventsAPI();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useTigerLilyAnimation(containerRef);

  useEffect(() => {
    console.log('🎭 CulturalEventsSection: Fetching upcoming events...');
    getUpcomingEvents().catch((err) => {
      console.error('❌ CulturalEventsSection: Failed to fetch events:', err);
    });
  }, [getUpcomingEvents]);

  useEffect(() => {
    console.log('🎭 CulturalEventsSection: Events state changed:', { 
      eventsCount: events.length, 
      loading, 
      error,
      events: events.map(e => ({ id: e._id, title: e.title }))
    });
  }, [events, loading, error]);

  // Sort events by date and show only first 3
  const sortedEvents = useMemo(() => {
    const sorted = sortEventsByDate(events);
    // Debug: log the sorted order
    if (sorted.length > 0) {
      console.log('📅 Sorted events by date:', sorted.map(e => ({
        title: e.title.substring(0, 30),
        date: e.date,
        day: new Date(e.date).getDate(),
        month: new Date(e.date).getMonth() + 1
      })));
    }
    return sorted;
  }, [events]);
  const featuredEvents = sortedEvents.slice(0, 3);

  return (
    <section className="cultural-events-section" ref={containerRef} id="cultural-events">
      <canvas 
        ref={canvasRef} 
        id="tiger-lily-canvas"
        className="tiger-lily-canvas"
      />
      <div className="cultural-events-container">
        <div className="cultural-events-section__header">
          <h2 className="section__title">{t("culturalEvents.title")}</h2>
          <p className="section__subtitle">
            {t("culturalEvents.subtitle")}
          </p>
        </div>

        {loading && (
          <div className="section__loading">
            <LoadingSpinner />
          </div>
        )}

        {error && (
          <div className="section__error">
            <ErrorMessage message={error} />
            <button 
              onClick={() => getUpcomingEvents()} 
              style={{ 
                marginTop: '16px', 
                padding: '10px 20px', 
                background: '#FF6B35', 
                color: 'white', 
                border: 'none', 
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && featuredEvents.length > 0 && (
          <>
            <div className="cultural-events-grid">
              {featuredEvents.map((event) => (
                <CulturalEventCard key={event._id} event={event} />
              ))}
            </div>

            <div className="cultural-events-section__footer">
              <button
                className="view-all-btn"
                onClick={() => navigate('/cultural-events')}
              >
                View All Events
                <span className="btn__arrow">→</span>
              </button>
            </div>
          </>
        )}

        {!loading && !error && featuredEvents.length === 0 && (
          <div className="section__empty">
            <p>No upcoming events at the moment. Check back soon!</p>
          </div>
        )}
      </div>
    </section>
  );
};

